import appConfig from 'appConfig';
import ExportResolver from 'resolvers/RTMResolvers/ExportResolver';
import { dispatch } from 'pluginServices/store';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import actionMessages from './messages';
import { pushToast, removeToast } from 'containers/ToastManager/actions';

/**
 * Subscribe to export service
 * Methods to monitor export reports
 */
export default (() => {
  /**
   * @type {ExportResolver}
   */
  let exportResolver;
  let isRegistered = false;

  const _subscriptions = {};
  const _rtmUnsubscribeFunctions = {};

  const unSubscribeToExport = (exportId) => {
    if (_subscriptions[exportId]) {
      _subscriptions[exportId] = null;
    }
    _forgetExport(exportId);
  };

  function _forgetExport(exportId) {
    if (_rtmUnsubscribeFunctions[exportId]) {
      // remove subscription from requestTable of RTM and
      // then the RTM calls author.export.unsubscribe
      _rtmUnsubscribeFunctions[exportId]();
      _rtmUnsubscribeFunctions[exportId] = null;
    }
  }

  function _createUnsubscribe(exportId, listener) {
    return () => {
      const listenerIndex = _subscriptions[exportId].indexOf(listener);
      _subscriptions[exportId].splice(listenerIndex, 1);
      if (_subscriptions[exportId].length === 0) {
        _forgetExport(exportId);
      }
    };
  }

  /**
   * Creates a subscription to an export.
   * @param {string} exportId
   * @param {function(*)} listener A callback invoke on subscription,
   * and every change to the export afterwards
   */
  const subscribeToExport = async (exportId, listener) => {
    _rtmUnsubscribeFunctions[exportId] =
      await exportResolver.authorExportSubscribe(exportId, listener);

    _subscriptions[exportId] = _subscriptions[exportId] || [];
    _subscriptions[exportId].push(listener);

    return _createUnsubscribe(exportId, listener);
  };

  /**
   * @desc check for error report
   * @param {Object} payload
   */
  const exportForReport = async (activityId, title, listener, outputType) => {
    if (!title) {
      title = "Preflight error report";
    }
    const { exportId } = await exportResolver.exportForReport(activityId, outputType);
    if (exportId) {
      dispatch(pushToast({
        message: `${intl.formatMessage(actionMessages.generatingReport)}${title}`,
        actions: [
          {
            text: 'CLOSE',
            fn: () => { },
          },
        ],
        toastId: activityId,
        type: 'sticky',
        waitForAction: true,
      }));
      subscribeToExport(exportId, listener);
    }
  };

  const requestPreflightReport = async (activityId, outputType) => {
    try {
      return await exportResolver.exportForReport(activityId, outputType);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  /**
 * @desc Get pre-signed URL
 * @param {exportId} exportId
 */
  const getPreSignedURL = async (exportId) => {
    const { response: preSignedURL } = await exportResolver.getPreSignedURL(exportId);
    return preSignedURL;
  };

  /**
   * Initiate an export for a Preview
   * @param {string} elementId
   * @param {string} elementType
   */
  const exportForPreview = async (elementId, elementType = 'ACTIVITY', threeLevelToc, exportType) => {
    const response = await exportResolver.exportForPreview(elementId, elementType, threeLevelToc, exportType);
    return response;
  };

  /**
   * @description ping the S3 location to see if the report folder is generated
   * Its wrapped in an IIFE to ensure the first call happens immediately
   * All subsequent pings will happen in 5s intervals
   * Pings will be cancelled after 10 minutes
   * @param {object}
   * @param {string} exportId
   * @param {string} title
   */
  const checkIfReportGenerated = async ({ activityId, exportId, title }, onCompleteDownloadReportCallback) => {
    const THIRTY_MINUTE = 30 * 60 * 1000;
    const FIVE_SECOND = 5 * 1000;
    const { url, header } = _getURlandHeader(exportId);
    let pingInterval = setInterval((function ping() {
      fetch(url, header).then((response) => {
        if ([200, 206].includes(response.status)) {
          clearInterval(pingInterval);
          pingInterval = undefined;
          // perform download of file
          _download(
            url,
            title,
            'xlsx',
            () => {
              dispatch(removeToast({ toastId: activityId }));
              onCompleteDownloadReportCallback();
            });
        }
      }).catch((error) => {
        clearInterval(pingInterval);
        pingInterval = undefined;
        dispatch(pushToast({ message: intl.formatMessage(actionMessages.reportFailed) }));
        dispatch(removeToast({ toastId: activityId }));
        throw new Error(`ePub error report generation failed, ${error}.`);
      });
      return ping;
    }()), FIVE_SECOND);

    setTimeout(() => {
      if (pingInterval) {
        clearInterval(pingInterval);
        dispatch(pushToast({ message: intl.formatMessage(actionMessages.reportFailed) }));
        dispatch(removeToast({ toastId: activityId }));
        throw new Error('ePub error report generation timed out');
      }
    }, THIRTY_MINUTE);
  };

  /**
 * @desc download excel reports from s3 locations
 * @param {string} endPoint
 * @param {string} fileName
 * @param {string} format
 */
  const _download = async (endPoint, fileName, format, afterDownloadCallBack) => {
    if (!fileName) {
      fileName = "Preflight error report";
    }
    fetch(endPoint, {
      method: 'GET',
    })
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${fileName}.${format}`);
        // Append to html link element page
        document.body.appendChild(link);
        // Start download
        link.click();
        // Clean up and remove the link
        link.parentNode.removeChild(link);
        // Releases the existing objectURL
        window.URL.revokeObjectURL(url);
      });
    afterDownloadCallBack();
  };

  /**
   * @description Construct URL anf headers
   * @param {string} exportId
   * @returns {object}
   */
  const _getURlandHeader = (exportId) => {
    const s3Folder = exportId.replace(/-/g, '');
    const s3Bucket = appConfig.getEpubURL();
    const REPORT_FILE_PATH = 'reports/Report.xlsx';
    const url = `${s3Bucket}/${s3Folder}/${REPORT_FILE_PATH}`;
    const headers = new Headers();
    headers.append('Range', 'bytes=0-1');
    return {
      url,
      header: {
        method: 'GET',
        headers,
      },
    };
  };

  return {
    isRegistered: () => isRegistered,
    register: (connectionProvider) => {
      isRegistered = true;
      exportResolver = new ExportResolver(connectionProvider);
    },
    subscribeToExport,
    unSubscribeToExport,
    exportForReport,
    exportForPreview,
    checkIfReportGenerated,
    getPreSignedURL,
    requestPreflightReport,
  };
})();
